// Account (api.ato.run cookie session) lane: session state, sign-in /
// sign-out, and the shared sign-in error banner. Since Desktop Runtime mode
// was retired this is the app's only session lane.
import { useCallback, useEffect, useRef, useState } from "react";
import type { AccountAuthState, AccountSession } from "../../auth/accountTypes";
import { fetchAccountSession, signOutAccount } from "../../auth/accountSession";
import { initiateSignIn } from "../../auth/accountCallback";
import {
  accountAuthEpoch,
  invalidateAccountAuthWork,
} from "../../auth/accountAuthEpoch";
import { setLibrarySession } from "../../api/libraryClient";
import { clearMyApps } from "@/components/personal-apps/myAppsStore";
import {
  endAuthRecoveryForSignOut,
  noteAuthRecoveryAccountState,
  registerAuthRecoveryAccount,
} from "../../auth/authRecovery";

export interface AccountController {
  accountState: AccountAuthState;
  /**
   * Raw state setter — used by screens with their own inline sign-in UI
   * (AccountSignIn, SettingsIndex) whose onAccountSignedIn
   * callbacks historically bypassed the library-session sink.
   */
  setAccountState: (state: AccountAuthState) => void;
  applyAccountState: (state: AccountAuthState) => void;
  commitSignedInSession: (state: AccountAuthState) => void;
  refreshAccountState: () => Promise<void>;
  signInError: string | null;
  dismissSignInError: () => void;
  handleAccountSignIn: () => Promise<void>;
  handleAccountSignOut: () => Promise<void>;
}

export function useAccountController(): AccountController {
  // Account auth state — completely separate from Runtime auth
  const [accountState, setAccountState] = useState<AccountAuthState>({
    status: "loading",
  });
  const lastKnownSession = useRef<AccountSession | null>(null);
  const inFlightRefresh = useRef<Promise<void> | null>(null);

  // Safety-net error surface for the secondary/contextual GitHub sign-in
  // buttons (GetRun, AppPage, ManagedCloudDemo, RunnersList, Activity,
  // ShowcaseFeedContainer) that call onSignIn as a bare fire-and-forget
  // `onClick` and have no local error UI of their own.
  // RepoRequestPage's flow is anonymous/email-only and never calls this.
  // initiateSignIn used
  // to be a synchronous, always-navigating `window.location.href` — it is
  // now async and can resolve `{ ok: false, message }` WITHOUT navigating
  // (network error, disabled provider, CORS/origin misconfiguration). Without
  // this, those buttons would go silently inert on failure. The three
  // components with their own inline sign-in error UI (AccountSignIn,
  // AccountStatus/Settings) handle this themselves instead and
  // are wired directly to `initiateSignIn`, not through this banner.
  //
  // This used to need care: AddCapsule sat on a Runtime route rendered through
  // a completely separate top-level `return` from every Account-mode route, so
  // the banner had to be wired into both mutually exclusive JSX trees.
  // Retiring Desktop Runtime mode removed that second tree, so a single
  // render site in App.tsx now reaches every route that can raise this error.
  const [signInError, setSignInError] = useState<string | null>(null);
  const handleAccountSignIn = useCallback(async () => {
    setSignInError(null);
    const result = await initiateSignIn();
    if (!result.ok) setSignInError(result.message);
  }, []);

  // Every account-state transition also tells the My Apps library client
  // (libraryClient.ts) whether to read/write localStorage (signed out) or
  // the server-side library (signed in) — see setLibrarySession's doc
  // comment. This is a thin notification sink, not a second auth mechanism:
  // it never fetches or derives session state itself, only reacts to state
  // this hook already computed, so the library client can never observe a
  // stale session.
  const stateRevision = useRef(0);
  const applyAccountState = useCallback((state: AccountAuthState) => {
    stateRevision.current += 1;
    setAccountState(state);
    if (state.status === "signed_in") lastKnownSession.current = state.session;
    if (state.status === "signed_out") lastKnownSession.current = null;
    setLibrarySession(state.status === "signed_in" ? state.session : null);
    noteAuthRecoveryAccountState(state);
  }, []);

  // Auth recovery commits through the same sink as every other sign-in.
  useEffect(() => registerAuthRecoveryAccount(applyAccountState), [applyAccountState]);

  /**
   * An in-page sign-in (Email OTP, passkey, Settings, onboarding) commits a
   * FRESHER session than any read already in flight, so it invalidates
   * pending auth work first — without this an older get-session resolving
   * afterwards could still overwrite the session the person just made.
   */
  const commitSignedInSession = useCallback(
    (state: AccountAuthState) => {
      if (state.status === "signed_in") invalidateAccountAuthWork();
      applyAccountState(state);
    },
    [applyAccountState],
  );

  const refreshAccountState = useCallback(async (): Promise<void> => {
    // Single-flight: focus + visibilitychange + online can fire together on
    // resume, and each must join the same read rather than triple it.
    if (inFlightRefresh.current) return inFlightRefresh.current;
    const epoch = accountAuthEpoch();
    const revision = stateRevision.current;
    const work = (async () => {
      try {
        const session = await fetchAccountSession();
        if (epoch !== accountAuthEpoch() || revision !== stateRevision.current) return;
        applyAccountState(
          session ? { status: "signed_in", session } : { status: "signed_out" },
        );
      } catch (err) {
        if (epoch !== accountAuthEpoch() || revision !== stateRevision.current) return;
        // A transient transport failure is not a signed-out session: keep the
        // last-known session rather than flashing the error state over it.
        if (lastKnownSession.current) return;
        applyAccountState({ status: "error", message: String(err) });
      }
    })().finally(() => {
      if (inFlightRefresh.current === work) inFlightRefresh.current = null;
    });
    inFlightRefresh.current = work;
    return work;
  }, [applyAccountState]);

  // Fetch account session on mount, then revalidate on every browser
  // lifecycle boundary where a paused timer or dropped connection could have
  // let the cookie session change behind our back (ato-pwa#352).
  useEffect(() => {
    const onFocus = () => void refreshAccountState();
    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshAccountState();
    };
    const onOnline = () => void refreshAccountState();
    const onPageShow = () => void refreshAccountState();
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisible);

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void refreshAccountState();
    }, 15 * 60 * 1000);

    void refreshAccountState();
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
    };
  }, [refreshAccountState]);

  const handleAccountSignOut = useCallback(async () => {
    // Explicit: a challenge in progress ends here, and nothing it later reads
    // may commit (signOutAccount advances the auth epoch).
    endAuthRecoveryForSignOut();
    await signOutAccount();
    applyAccountState({ status: "signed_out" });
    // The cached My Apps launcher is what lets `/` paint before the session
    // check answers, so it has to be erased HERE and not only by the screen
    // that reads it: signing out from Settings would otherwise leave the
    // previous account's app names on disk, ready to be painted at the next
    // cold start.
    clearMyApps();
    // Deliberately does NOT disconnect Runtime
  }, [applyAccountState]);

  const dismissSignInError = useCallback(() => setSignInError(null), []);

  return {
    accountState,
    setAccountState,
    applyAccountState,
    commitSignedInSession,
    refreshAccountState,
    signInError,
    dismissSignInError,
    handleAccountSignIn,
    handleAccountSignOut,
  };
}
